import React from 'react';
import { StatusBadge } from './StatusBadge';
import type { GRCDocument } from '../../../api/grcDocumentApi';

const PILLAR_COLORS: Record<string, string> = {
  G: 'var(--primary-600)',
  R: 'var(--ds-danger)',
  C: 'var(--primary-600)',
  Hybrid: 'var(--primary-600)',
};

interface DocumentCardProps {
  doc: GRCDocument;
  view: 'grid' | 'list';
  onView: (doc: GRCDocument) => void;
  onEdit: (doc: GRCDocument) => void;
  onReplace: (doc: GRCDocument) => void;
  onDelete: (doc: GRCDocument) => void;
}

function formatBytes(bytes: number | null): string {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const DocumentCard: React.FC<DocumentCardProps> = ({
  doc, view, onView, onEdit, onReplace, onDelete,
}) => {
  const isGrid = view === 'grid';

  const cardStyle: React.CSSProperties = isGrid
    ? {
        background: 'var(--ds-surface)',
        borderRadius: 'var(--ds-radius-lg)',
        border: '1px solid var(--ds-border)',
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        transition: 'box-shadow 0.2s',
        cursor: 'pointer',
      }
    : {
        background: 'var(--ds-surface)',
        borderRadius: 'var(--ds-radius-md)',
        border: '1px solid var(--ds-border)',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        transition: 'box-shadow 0.2s',
        cursor: 'pointer',
      };

  const btnStyle: React.CSSProperties = {
    background: 'none',
    border: '1px solid var(--ds-border-strong)',
    borderRadius: 'var(--ds-radius-sm)',
    padding: '4px 10px',
    fontSize: 12,
    cursor: 'pointer',
    color: 'var(--ds-text-secondary)',
  };

  return (
    <div style={cardStyle} onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)')} onMouseLeave={e => (e.currentTarget.style.boxShadow = 'none')}>
      {isGrid ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ds-text)', lineHeight: 1.3, flex: 1 }}>{doc.document_name}</div>
            <StatusBadge status={doc.status} />
          </div>
          <div style={{ fontSize: 12, color: 'var(--ds-text-muted)' }}>{doc.original_filename}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <span style={{
              fontSize: 11, fontWeight: 700, color: PILLAR_COLORS[doc.grc_pillar] ?? 'var(--ds-text-muted)',
              background: 'var(--gray-100)', borderRadius: 'var(--ds-radius-sm)', padding: '2px 6px',
            }}>{doc.grc_pillar}</span>
            <span style={{ fontSize: 11, color: 'var(--ds-text-muted)', background: 'var(--gray-100)', borderRadius: 'var(--ds-radius-sm)', padding: '2px 6px' }}>
              {doc.domain === 'data-privacy' ? 'Data & Privacy' : doc.domain === 'ai-technology' ? 'AI & Tech' : 'Both'}
            </span>
            <span style={{ fontSize: 11, color: 'var(--ds-text-muted)', background: 'var(--gray-100)', borderRadius: 'var(--ds-radius-sm)', padding: '2px 6px' }}>
              v{doc.version}
            </span>
            <span style={{ fontSize: 11, color: 'var(--ds-text-muted)' }}>{formatBytes(doc.file_size_bytes)}</span>
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 'auto' }}>
            <button style={btnStyle} onClick={e => { e.stopPropagation(); onView(doc); }}>View</button>
            <button style={btnStyle} onClick={e => { e.stopPropagation(); onEdit(doc); }}>Edit</button>
            <button style={btnStyle} onClick={e => { e.stopPropagation(); onReplace(doc); }}>Replace</button>
            <button style={{ ...btnStyle, color: 'var(--ds-danger-text)', borderColor: 'var(--ds-danger-border)' }} onClick={e => { e.stopPropagation(); onDelete(doc); }}>Delete</button>
          </div>
        </>
      ) : (
        <>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ds-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{doc.document_name}</div>
            <div style={{ fontSize: 12, color: 'var(--ds-text-muted)' }}>{doc.original_filename} &middot; {formatBytes(doc.file_size_bytes)}</div>
          </div>
          <span style={{
            fontSize: 11, fontWeight: 700, color: PILLAR_COLORS[doc.grc_pillar] ?? 'var(--ds-text-muted)',
            background: 'var(--gray-100)', borderRadius: 'var(--ds-radius-sm)', padding: '2px 6px', flexShrink: 0,
          }}>{doc.grc_pillar}</span>
          <span style={{ fontSize: 11, color: 'var(--ds-text-muted)', background: 'var(--gray-100)', borderRadius: 'var(--ds-radius-sm)', padding: '2px 6px', flexShrink: 0 }}>v{doc.version}</span>
          <StatusBadge status={doc.status} />
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button style={btnStyle} onClick={e => { e.stopPropagation(); onView(doc); }}>View</button>
            <button style={btnStyle} onClick={e => { e.stopPropagation(); onEdit(doc); }}>Edit</button>
            <button style={btnStyle} onClick={e => { e.stopPropagation(); onReplace(doc); }}>Replace</button>
            <button style={{ ...btnStyle, color: 'var(--ds-danger-text)', borderColor: 'var(--ds-danger-border)' }} onClick={e => { e.stopPropagation(); onDelete(doc); }}>Delete</button>
          </div>
        </>
      )}
    </div>
  );
};
